<template>
    <app-layout title="View Load">
        <page-header>
            <template #title>View Load</template>

            <template #actions>
                <template v-if="isAdmin">
                    <div class="flex flex-col sm:flex-row justify-between gap-x-4 gap-y-2">
                        <!-- Print button / dropdown -->
                        <!-- Retail -->
                        <jet-dropdown align="right">
                            <template #trigger>
                                <jet-tertiary-button size="small">
                                    <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                                    <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Print">
                                </jet-tertiary-button>
                            </template>
                            <template #content>
                                <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('summary')">Load Summary</div>
                                <div v-if="hasUnprintedWholesaleOrders" class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('unprinted_wholesale_orders')">Unprinted Wholesale Orders</div>
                                <div v-if="hasWholesaleOrders" class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('all_wholesale_orders')">All Wholesale Orders</div>
                                <div v-if="hasWholesaleOrders" class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('wholesale_labels')">Wholesale Labels</div>
                                <div v-if="hasUnprintedRetailOrders" class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('unprinted_retail_orders')">Unprinted Retail Orders</div>
                                <div v-if="hasRetailOrders" class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('all_retail_orders')">All Retail Orders</div>
                                <div v-if="hasRetailOrders" class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('retail_labels')">Retail Labels</div>
                            </template>
                        </jet-dropdown>
                    </div>

                    <div class="flex flex-col sm:flex-row justify-between gap-x-4 gap-y-2">
                        <jet-button type="button" class="leading-none inline-flex items-center" :href="editUrl">
                            Edit
                        </jet-button>

                        <jet-tertiary-button type="button" class="leading-none" :href="backUrl">
                            Close
                        </jet-tertiary-button>
                    </div>
                </template>

                <jet-tertiary-button v-else type="button" class="leading-none" :href="backUrl">
                    Close
                </jet-tertiary-button>
            </template>
        </page-header>

        <div class="flex flex-col px-3 md:px-4 lg:px-8 gap-y-4 ">
            <div class="md:flex">
                <div class="md:w-1/3">
                    <div v-if="load.routific_project_name" class="flex items-center mb-2">
                        <jet-label for="routific_project_name" value="Routific Project:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                        <div class="flex items-end whitespace-nowrap">
                            {{ load.routific_project_name }}
                        </div>
                    </div>
                    <div class="flex items-center mb-2">
                        <jet-label for="type" value="Load Type:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                        <div class="flex items-end whitespace-nowrap">
                            {{ load.type_def }}
                        </div>
                    </div>
                    <div class="flex items-center mb-2">
                        <jet-label for="date" value="Date:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                        <div class="flex items-end whitespace-nowrap">
                            {{ formatDate(load.date) }}
                        </div>
                    </div>
                    <div v-if="load.type == 'delivery'" class="flex items-center mb-2">
                        <jet-label for="route" value="Route Type:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                        <div class="flex items-end whitespace-nowrap">
                            {{ load.route_type_def != null ? load.route_type_def : '-'}}
                        </div>
                    </div>
                    <div v-if="load.type == 'delivery'" class="flex items-center mb-2">
                        <jet-label for="route" value="Route:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                        <div class="flex items-end whitespace-nowrap">
                            {{ load.route != null ? load.route : '-'}}
                        </div>
                    </div>
                </div>
                <div class="md:w-1/3">
                    <div v-if="load.type == 'delivery'" class="flex items-center mb-2">
                        <jet-label for="truck" value="Truck:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                        <div class="flex items-end whitespace-nowrap">
                            {{ load.truck != null ? load.truck : '-'}}
                        </div>
                    </div>
                    <div v-if="load.type == 'delivery'" class="flex items-center mb-2">
                        <jet-label for="driver" value="Driver:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                        <div class="flex items-end whitespace-nowrap">
                            {{ load.driver != null ? load.driver : '-'}}
                        </div>
                    </div>
                    <div v-if="load.type == 'delivery'" class="flex items-center mb-2">
                        <jet-label for="departure_time" value="Departure:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                        <div class="flex items-end whitespace-nowrap">
                            {{ (load.departure_time != null) ? formatTime(load.departure_time) : '-' }}
                        </div>
                    </div>
                    <div v-if="load.type == 'pickup'" class="flex items-center mb-2">
                        <jet-label for="location" value="Location:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                        <div class="flex items-end whitespace-nowrap">
                            {{ load.location_name }}
                        </div>
                    </div>
                    <div v-if="load.type == 'pickup'" class="flex items-center mb-2">
                        <jet-label for="shipping_time" value="Pickup Time:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                        <div class="flex items-end whitespace-nowrap">
                            {{ load.shipping_time_label}}
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex items-end gap-x-4">
                <div v-if="!isOnlyPicker" class="md:w-1/3 lg:w-1/4 py-2">
                    <jet-label for="loader_id" value="Loaded By:" class="w-32 align-top leading-7 whitespace-nowrap" />
                    <div class="flex flex-1 flex-col">
                        <multiselect
                            class="multi-lg h-7 text-base"
                            v-model="load.loader_id"
                            :options="loaderOptions"
                            :disabled="loadedOrders && load.loader_id != null"
                        />
                        <jet-input-error :message="$page.props.errors.loader_id" class="m1" />
                    </div>
                </div>

                <div class="md:w-1/3 lg:w-1/4 py-2">
                    <jet-label for="loader_id" value="Order Type:" class="w-32 align-top leading-7 whitespace-nowrap" />
                    <div class="flex flex-1 flex-col">
                        <multiselect
                            class="multi-lg h-7 text-base"
                            v-model="orderType"
                            :options="orderTypeOptions"
                            placeholder="All"
                        />
                    </div>
                </div>

                <div class="md:w-1/3 lg:w-1/4 py-2 text-sm">
                    <div class="flex gap-x-2">
                        <div>
                            <div class="flex gap-x-2"><div class="w-3 h-3 mt-1 border-gray-500 rounded-full bg-red-500 bg-opacity-20"></div> Picked</div>
                            <div class="flex gap-x-2"><div class="w-3 h-3 mt-1 border-gray-500 rounded-full bg-yellow-400 bg-opacity-20"></div> Checked</div>
                        </div>
                        <div>
                            <div class="flex gap-x-2"><div class="w-3 h-3 mt-1 border-gray-500 rounded-full bg-blue-500 bg-opacity-20"></div> Packaged</div>
                            <div class="flex gap-x-2"><div class="w-3 h-3 mt-1 border-gray-500 rounded-full bg-green bg-opacity-20"></div> Loaded</div>
                        </div>
                    </div>
                </div>
            </div>
            
            <load-order-list
                :orderType="orderType"
                :loadOrders="load.load_orders"
                :load_id="load.id"
                :load_loader_id="load.loader_id"
                :load_type="load.type"

                @updatePickingData="updatePickingData"
            />

            <div v-if="pickingData" class="w-full mt-6">
                <div class="text-lg font-bold">
                    Picking Statistics
                </div>

                <picking-data :pickingData="pickingData" />
            </div>
        </div>

        <div v-if="isAdmin" class="text-center py-24" >
            <div class="flex justify-center gap-x-8">
                <jet-button v-if="true" class="leading-none" @click="markOrdersShippedAndInvoiced">
                    Mark Shipped &amp; Invoiced
                </jet-button>

                <jet-danger-button @click="showDeleteModal = true">
                    Delete Load
                </jet-danger-button>
            </div>
        </div>

        <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal=false">
            <template #title>
                Delete Load
            </template>

            <template #content>
                Are you sure you would like to delete this load?
            </template>

            <template #footer>
                <jet-danger-button @click="deleteLoad">
                    Yes
                </jet-danger-button>

                <jet-tertiary-button class="ml-2" @click="showDeleteModal=false">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>

        <jet-dialog-modal :show="showMarkShippedAndInvoicedModal" :closeable="!shippingAndInvoicing" @close="resetMarkShippedAndInvoicedModal()">
            <template #title>
                Ship &amp; Invoice
            </template>

            <template #content>
            
                <div v-if="shippingAndInvoicingErrorMsg">
                    {{ shippingAndInvoicingErrorMsg }}
                </div>
                <div v-else-if="shippingAndInvoicing">
                    <div class="text-center text-lg font-bold flex flex-col items-center justify-center">
                        <img src="/images/icons/loading-pulse.svg" height="128" width="128" class="" />
                        <p>Shipping &amp; Invoicing ...</p>
                    </div>
                </div>
                <div v-else-if="shippedAndInvoicedResponse === null">
                    Are you sure you want to ship &amp; invoice the {{ uninvoicedOrders }} uninvoiced order{{ uninvoicedOrders == 1 ? '' : 's' }} on this load?
                </div>
                <div v-else>
                    <!-- Processed Orders -->
                    <div v-if="shippedAndInvoicedResponse.processed.total > 0">
                        <div>Processed Orders ({{ shippedAndInvoicedResponse.processed.total }})</div>
                        
                        <ul class="list-disc list-outside">
                            <li class="ml-8">
                                {{ shippedAndInvoicedResponse.processed.orders.join(', ') }}
                            </li>
                        </ul>
                    </div>

                    <br v-if="shippedAndInvoicedResponse.processed.total > 0 && shippedAndInvoicedResponse.unprocessed.total > 0"/>

                    <!-- Unprocessed Orders -->
                    <div v-if="shippedAndInvoicedResponse.unprocessed.total > 0">
                        Unprocessed Orders ({{ shippedAndInvoicedResponse.unprocessed.total }})

                        <ul class="list-disc list-outside">
                            <template v-for="(order_nums, response) in shippedAndInvoicedResponse.unprocessed.orders">
                                <li v-if="order_nums.length > 0" :key="response" class="ml-8">
                                    <div v-if="response == 'could_not_be_processed'">
                                        Could Not Be Processed ({{ order_nums.length }})
                                        <div class="text-xs">There was an issue processing payment. Please try again.</div>
                                    </div>
                                    <div v-else-if="response == 'invalid'">
                                        Invalid Credit Card ({{ order_nums.length }})
                                        <div class="text-xs">The customer's credit card on file is invalid. Please contact the customer and update their credit card information before shipping &amp; invoicing.</div>
                                    </div>
                                    <div v-else-if="response == 'missing'">
                                        Credit Card Missing ({{ order_nums.length }})
                                        <div class="text-xs">The customer is a credit card customer but doesn't have a credit card on file. Please contact the customer and add their credit card information before shipping &amp; invoicing.</div>
                                    </div>
                                    <div v-else>
                                        Unknown Issue ({{ order_nums.length }})
                                        <div class="text-xs">There was an unknown issue when processing payment.</div>
                                    </div>

                                    <ul class="list-disc list-outside">
                                        <li class="ml-8">
                                            {{ order_nums.join(', ') }}
                                        </li>
                                    </ul>
                                </li>
                            </template>
                        </ul>
                    </div>
                </div>
            </template>

            <template #footer>
                <div v-if="!shippingAndInvoicingErrorMsg && !shippingAndInvoicing && shippedAndInvoicedResponse === null">
                    <jet-button @click="handleShipAndInvoice">
                        Yes
                    </jet-button>

                    <jet-tertiary-button class="ml-2" @click="resetMarkShippedAndInvoicedModal()">
                        No
                    </jet-tertiary-button>
                </div>
                <div v-else>
                    <jet-tertiary-button @click="resetMarkShippedAndInvoicedModal()" :disabled="shippingAndInvoicing">
                        Close
                    </jet-tertiary-button>
                </div>
            </template>
        </jet-dialog-modal>

    </app-layout>
</template>

<script>
import { usePage, Link } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import AppLayout from '@/Layouts/AppLayout.vue';
import { DateTime } from "luxon";
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInputError from '@/Jetstream/InputError';
import Multiselect from '@vueform/multiselect';
import JetButton from '@/Jetstream/Button';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetDropdown from '@/Jetstream/Dropdown';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import LoadOrderList from '@/Pages/Loads/Partials/LoadOrderList.vue';
import PickingData from '@/Pages/Loads/Partials/PickingData.vue';

import '@vueform/multiselect/themes/default.css';

export default {
    setup () {
        const { isAdmin, isOnlyPicker } = UseUserAuth(usePage().props.value.user);

        return { isAdmin, isOnlyPicker }
    },

    components: {
        Link,
        AppLayout,
        PageHeader,
        JetLabel,
        JetInputError,
        Multiselect,
        JetButton,
        JetConfirmationModal,
        JetSecondaryButton,
        JetDangerButton,
        JetDialogModal,
        JetDropdown,
        JetTertiaryButton,
        LoadOrderList,
        PickingData,
    },

    props: ['load', 'loaderOptions', 'initPickingData', 'initOrderType'],

    data() {
        return {
            showDeleteModal: false,
            showEmailModal: false,
            showMarkShippedAndInvoicedModal: false,
            shippingAndInvoicing: false,
            shippingAndInvoicingErrorMsg: null,
            shippedAndInvoicedResponse: null,
            emailType: null,

            orderType: this.initOrderType,

            pickingData: this.initPickingData,
        }
    },

    mounted() {
        this.orderType = (this.initOrderType && this.orderTypeOptions.some(opt => opt.value === this.initOrderType)) ? this.initOrderType : null;
    },

    methods: {
        handlePrint(type) {
            window.open((route('loads.print', { load: this.load.id, printType: type} )), "_blank");
            if (type == 'unprinted_wholesale_orders' || type == 'all_wholesale_orders' || type == 'unprinted_retail_orders' || type == 'all_retail_orders') {
                setTimeout(()=> {
                    this.$inertia.reload({ only: ['load'] });
                }, 250);
            }
        },

        deleteLoad () {
            this.$inertia.visit(route('loads.destroy', this.load.id), {method: 'delete'});
        },

        autoSaveLoader () {
            axios.post(route('loads.update_loader', {load: this.load.id, loader_id: this.load.loader_id}));
        },

        updateOrderType () {
            axios.post(route('orders.update_order_type', {order_type: this.orderType}));
        },

        markOrdersShippedAndInvoiced() {
            this.showMarkShippedAndInvoicedModal = true;
        },

        resetMarkShippedAndInvoicedModal () {
            this.showMarkShippedAndInvoicedModal = false;
            this.shippingAndInvoicing = false;
            this.shippedAndInvoicedResponse = null;
            this.shippingAndInvoicingErrorMsg = null;
        },

        handleShipAndInvoice() {
            this.shippingAndInvoicing = true;

            axios.post(route('loads.mark.shipped_and_invoiced', { load: this.load.id }))
            .then((response) => {
                this.$inertia.reload({ only: ['load'] });

                this.shippedAndInvoicedResponse = response.data;

                this.shippingAndInvoicing = false;
            })
            .catch(() => {
                this.shippingAndInvoicingErrorMsg = "There was an unknown issue when shipping and invoicing.";

                this.shippingAndInvoicing = false;
            });
        },

        emailOrder(type) {
            this.emailType = type;
            this.showEmailModal = true;
        },

        handleEmail() {
            this.$inertia.visit(route('orders.email', { order: this.order.id, type: this.emailType }));
        },

        cancelEmail () {
            this.emailType = null;
            this.showEmailModal = false;
        },

        formatDate (date) {
            return DateTime.fromISO(date).toFormat('MMM d, yyyy');
        },

        formatTime(timestamp) {
            let date = DateTime.fromSQL(timestamp).invalid ? DateTime.fromISO(timestamp) : DateTime.fromSQL(timestamp);

            if (date.c.hour === 0 && date.c.minute === 0 && date.c.second === 0) {
                return '';
            }

            return date.toFormat('h:mm a');
        },

        updatePickingData (newPickingData) {
            this.pickingData = newPickingData;
        }
    },

    computed: {
        backUrl () {
            const params = route().params;

            if (params.return) {
                return params.return;
            }

            return route('loads.index', {
                page: this.$page.props.page.loads,
            });
        },

        editUrl () {
            const params = route().params;

            return route('loads.edit', {
                load: this.load.id,
                return: params.return,
            });
        },

        canInvoice() {
            let today = DateTime.now().startOf('day');
            let shipDate = DateTime.fromISO(this.load.date);
            
            if (today > shipDate) return false;
            return true;
        },

        showMarkShippedAndInvoiced() {
            return this.canInvoice;
        },

        hasUnprintedWholesaleOrders() {
            return this.load.load_orders.filter(lo => lo.order && lo.order.is_wholesale == 1 && lo.order.status == 0).length > 0;
        },

        hasWholesaleOrders() {
            return this.load.load_orders.filter(lo => lo.order && lo.order.is_wholesale == 1).length > 0;
        },

        hasUnprintedRetailOrders() {
            return this.load.load_orders.filter(lo => lo.order && lo.order.is_wholesale == 0 && lo.order.status == 0).length > 0;
        },

        hasRetailOrders() {
            return this.load.load_orders.filter(lo => lo.order && lo.order.is_wholesale == 0).length > 0;
        },

        uninvoicedOrders() {
            return this.load.load_orders.filter(lo => lo.order && (lo.order.status == 0 || lo.order.status == 1)).length;
        },

        loadedOrders() {
            return this.load.load_orders.filter(lo => lo.order && lo.order.loaded_at && lo.order.picked_at).length > 0;
        },

        hasNonSchoolWholesaleOrders() {
            return this.load.load_orders.filter(lo => lo.order && lo.order.is_wholesale == 1 && lo.order.customer?.type !== 'School').length > 0;
        },

        hasSchoolOrders() {
            return this.load.load_orders.filter(lo => lo.order && lo.order.is_wholesale == 1 && lo.order.customer?.type === 'School').length > 0;
        },

        orderTypeOptions() {
            let options = [];

            if (this.hasRetailOrders) options.push({ label: 'Retail', value: 'retail' });
            if (this.hasNonSchoolWholesaleOrders) options.push({ label: 'Wholesale', value: 'wholesale' });
            if (this.hasSchoolOrders) options.push({ label: 'School', value: 'school' });

            return options;
        },
    },

    watch: {
        'load.loader_id' () {
            this.autoSaveLoader();
        },

        orderType () {
            this.updateOrderType()
        }
    }
}
</script>

<style scoped>

</style>
